import { useRef, useState, useEffect, useCallback } from "react";

// ── useInView — fires once when element enters viewport ───────────────────────
// Uses requestAnimationFrame for performance (no reflow on every scroll tick)
export function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.unobserve(el); // fire once only — better performance
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" },
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, inView];
}

// ── useFetch — generic data fetching with loading/error state ─────────────────
export function useFetch(fetchFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchFn()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}

// ── useCategories — cached categories shared across components ────────────────
let _categoriesCache = null;
let _categoriesPromise = null;

export function useCategories() {
  const [categories, setCategories] = useState(_categoriesCache || []);
  const [loading, setLoading] = useState(!_categoriesCache);

  useEffect(() => {
    if (_categoriesCache) {
      setCategories(_categoriesCache);
      setLoading(false);
      return;
    }

    if (!_categoriesPromise) {
      _categoriesPromise = fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/categories`,
      ).then((r) => r.json());
    }

    _categoriesPromise
      .then((d) => {
        _categoriesCache = d.data || [];
        setCategories(_categoriesCache);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  return { categories, loading };
}

// ── Inject favicons dynamically ───────────────────────────────────────────────
export function injectFavicons(favicons, apiBase) {
  if (!favicons) return;

  // Remove existing dynamic favicons
  document
    .querySelectorAll("link[data-dynamic-favicon]")
    .forEach((el) => el.remove());

  const add = (rel, href, sizes, type) => {
    if (!href) return;
    const link = document.createElement("link");
    link.rel = rel;
    link.href = `${apiBase}${href}`;
    link.setAttribute("data-dynamic-favicon", "1");
    if (sizes) link.sizes = sizes;
    if (type) link.type = type;
    document.head.appendChild(link);
  };

  add("icon", favicons.ico, null, "image/x-icon");
  add("icon", favicons.png16, "16x16", "image/png");
  add("icon", favicons.png32, "32x32", "image/png");
  add("apple-touch-icon", favicons.appleTouchIcon, "180x180", null);
}
